import React, { useLayoutEffect, useState } from "react";

import StarIcon from "@uui/Stars01";
import { find, some } from "lodash/fp";
import { useLocation, useSearchParams } from "react-router-dom";

import styled from "@mui/material/styles/styled";

import Tip from "@app/analytics/components/Tip";
import useIntegration from "@app/analytics/hooks/integrations/useIntegration";
import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import ConnectableIntegrationProvider from "@app/analytics/onboarding/components/ConnectableIntegrationProvider";
import NextButton from "@app/analytics/onboarding/components/NextButton";
import { TextButton } from "@app/analytics/onboarding/components/styles";
import useOnboardingStore from "@app/analytics/onboarding/hooks/useOnboardingStore";
import {
  StepCardContent,
  StepHeader,
  StepDescription,
  StepTitle,
  StepFooter,
} from "@app/analytics/onboarding/steps/styles";
import {
  EMAIL_PROVIDERS,
  DATA_INGEST_PROVIDERS,
  INTEGRATION_PROVIDERS_BY_ID,
  PRODUCTIVITY_PROVIDERS,
} from "@app/common/constants/providers";
import { toast } from "@app/common/hooks/useToasts";
import type {
  IntegrationProvider,
  IntegrationProviderId,
} from "@app/common/types/providerTypes";
import { withQueryParamsUrl } from "@app/common/utils";
import { usePageTitle } from "@app/utils/hooks";

import Stack, { Column } from "@rui/Stack";

const ProviderListWrapper = styled(Stack)(({ theme }) => ({
  flexDirection: "column",
  flexWrap: "wrap",
  width: "100%",
  alignItems: "center",
  justifyContent: "center",
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
  },
}));

const IntegrationsStep = () => {
  usePageTitle("Welcome - Integrations");

  const [pendingIntegration, setPendingIntegration] =
    useState<IntegrationProviderId | null>(null);

  const location = useLocation();
  const [queryParams] = useSearchParams();

  const { goToNextStep } = useOnboardingStore();

  const { getEnableUrl, isProviderConnected } = useIntegrations();
  const { enable, authorize } = useIntegration();

  const connectAction = queryParams.get("action");
  const isConnectSuccess = queryParams.get("success") === "true";

  useLayoutEffect(() => {
    if (!connectAction) return;

    const integration = find({ connectAction }, PRODUCTIVITY_PROVIDERS);

    if (!integration) return;

    if (isConnectSuccess) {
      toast.success(`${integration.displayNameShort} connected!`);
    } else {
      toast.error(
        `There was an error connecting ${integration.displayNameShort}. Please try again or contact support@read.ai if the problem persists.`
      );
    }
  }, [connectAction, isConnectSuccess]);

  const handleAuthorizeIntegration = (provider: IntegrationProvider) => {
    authorize.mutate({
      providerId: provider.id,
      // return to this route with all the existing query params, while omitting any from
      // a previous integration connection
      returnRoute: withQueryParamsUrl(location.pathname, {}, location.search, [
        "action",
        "success",
      ]),
      scope: "user",
    });
  };

  const handleConnect = (providerId: IntegrationProviderId) => {
    setPendingIntegration(providerId);
    const enableUrl = getEnableUrl({ providerId });
    const providerConfig = INTEGRATION_PROVIDERS_BY_ID[providerId];
    // if we can enable the integration without pushing them through another auth flow, try to do that.
    if (enableUrl) {
      enable.mutate(enableUrl, {
        onSuccess: () => {
          toast.success(`${providerConfig.displayNameShort} connected!`);
          setPendingIntegration(null);
        },
        onError: () => handleAuthorizeIntegration(providerConfig),
      });
    } else {
      handleAuthorizeIntegration(providerConfig);
    }
  };

  const hasAtLeastOneIntegrationConnected = some(
    ({ id }) => isProviderConnected({ providerId: id }),
    PRODUCTIVITY_PROVIDERS
  );

  const handleGoToNextStep = () =>
    goToNextStep(hasAtLeastOneIntegrationConnected ? "complete" : "skipped");

  return (
    <>
      <StepCardContent spacing={4}>
        <StepHeader>
          <StepTitle>Email & Messaging Readouts</StepTitle>
          <StepDescription>
            Streamline communication with automatic topic summaries across email
            or messaging. When you connect a source below, Read will deliver a
            new Readout whenever a new, important topic is detected.
          </StepDescription>
        </StepHeader>
        <ProviderListWrapper spacing={2}>
          <Column spacing={1} width="200px" maxWidth="100%">
            {EMAIL_PROVIDERS.map((provider) => (
              <ConnectableIntegrationProvider
                key={provider.id}
                provider={provider}
                onConnect={handleConnect}
                isConnecting={pendingIntegration === provider.id}
              />
            ))}
          </Column>
          <Column spacing={1} width="200px" maxWidth="100%">
            {DATA_INGEST_PROVIDERS.map((provider) => (
              <ConnectableIntegrationProvider
                key={provider.id}
                provider={provider}
                onConnect={handleConnect}
                isConnecting={pendingIntegration === provider.id}
              />
            ))}
          </Column>
        </ProviderListWrapper>
        <Tip Icon={StarIcon}>
          {hasAtLeastOneIntegrationConnected
            ? "Nice - you connected an integration! Connect more if you'd like."
            : "Read recommends connecting at least one integration!"}
        </Tip>
      </StepCardContent>
      <StepFooter>
        <TextButton onClick={handleGoToNextStep}>I'll do this later</TextButton>
        <NextButton onClick={handleGoToNextStep} />
      </StepFooter>
    </>
  );
};

export default IntegrationsStep;
